import { useState, useEffect, useRef } from 'react';
import { Pill, Check, X, Clock, Stethoscope, Plus, Edit3, Trash2, AlertTriangle, Calendar, ChevronRight, Undo2 } from 'lucide-react';
import { patientApi } from '../api';
import PrescribedNotTracked from '../components/PrescribedNotTracked';
import type { MedicineReminder } from '../types';
import Spinner from '../../../components/ui/Spinner';
import { useStore, store } from '../state/patientProductionStore';
import { useToast } from '../../../components/ui/Toast';

const FREQUENCIES = ['ONCE_DAILY', 'TWICE_DAILY', 'THRICE_DAILY', 'FOUR_TIMES', 'WEEKLY', 'AS_NEEDED'];
const INSTRUCTIONS = ['After meals', 'Before meals', 'Empty stomach', 'At bedtime', 'With water', 'As directed'];
type Tab = 'ALL' | 'MEDICINE' | 'DIET' | 'ROUTINE' | 'ACTIVE' | 'COMPLETED' | 'PRESCRIBED';

/**
 * The server sends `kind` on every reminder — MEDICINE, DIET or ROUTINE — and
 * this page ignored it, so a diet goal and a daily routine both listed as
 * medicines to take. On the demo account that was 12 of 16 items. The mobile
 * app has had these three tabs all along.
 */
const KIND_LABEL: Record<string, string> = {
  MEDICINE: 'Medicine', DIET: 'Diet', ROUTINE: 'Routine',
};

function parseTimes(times: any): string[] {
  if (Array.isArray(times)) return times;
  if (typeof times === 'string') { try { const p = JSON.parse(times); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
}

function freqLabel(f: string) { return f.replace(/_/g, ' ').toLowerCase(); }

function isRefillSoon(med: MedicineReminder) {
  if (med.status !== 'ACTIVE' || !med.endDate) return false;
  const diff = (new Date(med.endDate).getTime() - Date.now()) / 86400000;
  return diff >= 0 && diff <= 7;
}

/** `YYYY-MM-DD` for a date, in the viewer's own timezone. */
function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** The last 7 calendar days, oldest first. */
function last7Days(): string[] {
  const out: string[] = [];
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    out.push(dayKey(d));
  }
  return out;
}

/**
 * A course whose end date has passed is over.
 *
 * The server never flips `status` to COMPLETED on its own — it stays ACTIVE
 * until the patient marks it — so `endDate` is the only signal that a course
 * has finished. Reading `status` alone kept a course that ended six days ago
 * in "Active Medicines" and asked for today's dose.
 */
function hasEnded(med: MedicineReminder): boolean {
  if (med.status === 'COMPLETED') return true;
  if (!med.endDate) return false;
  return med.endDate.slice(0, 10) < dayKey(new Date());
}

/** A course that has not begun yet asks for nothing today. */
function notStarted(med: MedicineReminder): boolean {
  if (!med.startDate) return false;
  return med.startDate.slice(0, 10) > dayKey(new Date());
}

/** Running today: begun, not finished, and not marked complete. */
function isRunning(med: MedicineReminder): boolean {
  return med.status === 'ACTIVE' && !hasEnded(med) && !notStarted(med);
}

/**
 * Adherence over the last 7 CALENDAR DAYS.
 *
 * This used to be `logs.slice(0, 7)` — the seven most recent log entries,
 * whatever days they fell on — so seven logs from three months ago read as a
 * perfect week, and a day with no dose simply vanished instead of counting
 * against the course. Returns null when the week holds no logs at all, which
 * is "nothing recorded", not "nothing taken".
 */
function adherenceRate(logs: MedicineReminder['adherenceLogs']) {
  const week = new Set(last7Days());
  const recent = (logs || []).filter(l => week.has((l.logDate || '').slice(0, 10)));
  if (!recent.length) return null;
  return Math.round((recent.filter(l => l.taken).length / recent.length) * 100);
}

export default function MedicineReminders() {
  const toast = useToast();
  const [meds, setMeds] = useState<MedicineReminder[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('ALL');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ medicineName: '', dosage: '', frequency: 'ONCE_DAILY', times: ['08:00'], startDate: '', endDate: '', instructions: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [todayDoses, setTodayDoses] = useState<(MedicineReminder & { _time: string; _status: 'PENDING' | 'TAKEN' | 'SKIPPED' | 'MISSED' })[]>([]);
  const [skipModal, setSkipModal] = useState<{ id: string; time: string } | null>(null);
  const SKIP_REASONS = ['Forgot', 'Nausea', 'Side effects', 'Out of stock', 'Doctor advised', 'Other'];

  const storeReminders = useStore(s => s.medicineReminders);
  const [loadError, setLoadError] = useState(false);

  const storeToApiShape = (r: typeof storeReminders[number]): MedicineReminder => ({
    id: r.id,
    medicineName: r.name,
    dosage: r.dosage,
    frequency: r.frequency,
    times: r.times,
    startDate: r.startDate,
    endDate: r.endDate,
    status: r.active ? 'ACTIVE' : 'COMPLETED',
    instructions: r.instructions,
    prescriptionId: r.prescriptionId,
    source: r.prescriptionId ? 'PRESCRIPTION' : 'MANUAL',
    // The device store only ever held medicines; it has no diet or routine
    // items, so saying MEDICINE here is the truth rather than a default.
    kind: 'MEDICINE',
    missedDoseAlert: true,
    adherenceLogs: (r.takenLog || []).map((l, i) => ({
      id: `log-${r.id}-${i}`,
      logDate: l.date,
      taken: l.taken,
      takenAt: l.taken ? l.time : undefined,
    })),
  });

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const load = async () => {
    setLoading(true);
    let apiData: MedicineReminder[] = [];
    try {
      apiData = await patientApi.getMedicines();
      if (aliveRef.current) setLoadError(false);
    } catch {
      // Server list unavailable — locally saved reminders still render below.
      if (aliveRef.current) setLoadError(true);
    }
    if (!aliveRef.current) return;
    const storeMapped = storeReminders.map(storeToApiShape);
    const apiIds = new Set(apiData.map(m => m.id));
    const merged = [...apiData, ...storeMapped.filter(m => !apiIds.has(m.id))];
    setMeds(merged);
    buildTodayDoses(merged);
    setLoading(false);
  };

  const buildTodayDoses = (list: MedicineReminder[]) => {
    const today = new Date().toISOString().split('T')[0];
    const nowHHMM = `${new Date().getHours().toString().padStart(2, '0')}:${new Date().getMinutes().toString().padStart(2, '0')}`;
    const doses: typeof todayDoses = [];
    list.filter(isRunning).forEach(m => {
      const times = parseTimes(m.times);
      const todayLogs = (m.adherenceLogs || []).filter(l => l.logDate?.startsWith(today));
      times.forEach(t => {
        // `scheduledTime` is the slot this log answers. The fallback used to be
        // `todayLogs[tIdx]` — the nth log for the nth slot — which ticks the
        // wrong dose whenever a slot is skipped or logged out of order.
        const matchingLog = todayLogs.find(l => (l.scheduledTime ?? l.takenAt) === t);
        let status: 'PENDING' | 'TAKEN' | 'SKIPPED' | 'MISSED' = 'PENDING';
        if (matchingLog) {
          status = matchingLog.taken ? 'TAKEN' : 'SKIPPED';
        } else if (t < nowHHMM) {
          status = 'MISSED';
        }
        doses.push({ ...m, _time: t, _status: status });
      });
    });
    doses.sort((a, b) => a._time.localeCompare(b._time));
    setTodayDoses(doses);
  };

  useEffect(() => { load(); }, [storeReminders.length]);

  const activeMeds = meds.filter(isRunning);
  // Null when the week holds no logs — the card then says so rather than
  // printing "0%", which reads as every dose missed.
  const totalAdherence = (() => {
    const week = new Set(last7Days());
    const all = meds
      .flatMap(m => m.adherenceLogs || [])
      .filter(l => week.has((l.logDate || '').slice(0, 10)));
    if (!all.length) return null;
    return Math.round((all.filter(l => l.taken).length / all.length) * 100);
  })();
  const prescribedCount = meds.filter(m => m.source === 'PRESCRIPTION').length;

  const filtered = meds.filter(m => {
    if (tab === 'ACTIVE') return m.status === 'ACTIVE';
    if (tab === 'COMPLETED') return m.status === 'COMPLETED';
    if (tab === 'PRESCRIBED') return m.source === 'PRESCRIPTION';
    if (tab === 'MEDICINE' || tab === 'DIET' || tab === 'ROUTINE') return m.kind === tab;
    return true;
  });

  // A kind chip only appears once the account actually holds that kind, so an
  // account with medicines alone is not offered two tabs that say nothing.
  const kindsPresent = ['MEDICINE', 'DIET', 'ROUTINE'].filter(
    k => meds.some(m => m.kind === k),
  );
  const tabs: Tab[] = [
    'ALL',
    ...(kindsPresent.length > 1 ? (kindsPresent as Tab[]) : []),
    'ACTIVE', 'COMPLETED', 'PRESCRIBED',
  ];

  const openCreate = () => {
    setEditId(null);
    setForm({ medicineName: '', dosage: '', frequency: 'ONCE_DAILY', times: ['08:00'], startDate: '', endDate: '', instructions: '', notes: '' });
    setShowForm(true);
  };

  const openEdit = (med: MedicineReminder) => {
    setEditId(med.id);
    const t = parseTimes(med.times);
    setForm({ medicineName: med.medicineName, dosage: med.dosage || '', frequency: med.frequency, times: t.length ? t : ['08:00'], startDate: med.startDate?.split('T')[0] || '', endDate: med.endDate?.split('T')[0] || '', instructions: med.instructions || '', notes: med.notes || '' });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.medicineName || !form.dosage) return;
    setSaving(true);
    try {
      const payload = { ...form, times: JSON.stringify(form.times) };
      if (editId) await patientApi.updateMedicine(editId, payload);
      else await patientApi.createMedicine(payload);
      setShowForm(false);
      toast.success(editId ? 'Medicine reminder updated' : 'Medicine reminder added');
      load();
    } catch (e: any) { toast.error(e.message || 'Could not save medicine reminder.'); }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this medicine reminder?')) return;
    const isStoreReminder = storeReminders.some(r => r.id === id);
    if (isStoreReminder) {
      store.deleteMedicineReminder(id);
    } else {
      try {
        await patientApi.deleteMedicine(id);
        toast.success('Medicine reminder deleted');
        load();
      } catch (e: any) { toast.error(e.message || 'Could not delete medicine reminder.'); }
    }
  };

  const handleLog = async (id: string, taken: boolean, scheduledTime?: string, reason?: string) => {
    const isStoreReminder = storeReminders.some(r => r.id === id);
    if (isStoreReminder) {
      const logTime = scheduledTime || `${new Date().getHours().toString().padStart(2, '0')}:${new Date().getMinutes().toString().padStart(2, '0')}`;
      store.logMedicineDose(id, logTime, taken, reason);
    } else {
      try {
        await patientApi.logAdherence(id, { taken, scheduledTime, skippedNote: reason, source: 'REMINDER' });
        load();
      } catch (e: any) { toast.error(e.message || 'Could not log dose.'); }
    }
  };

  const addTimeSlot = () => setForm(f => ({ ...f, times: [...f.times, '12:00'] }));
  const removeTimeSlot = (i: number) => setForm(f => ({ ...f, times: f.times.filter((_, idx) => idx !== i) }));
  const updateTime = (i: number, v: string) => setForm(f => ({ ...f, times: f.times.map((t, idx) => idx === i ? v : t) }));

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Medicine Reminders</h1>
          <p className="text-sm text-gray-500 mt-0.5">Track medications & adherence</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-xl hover:bg-blue-700 transition-colors">
          <Plus className="w-4 h-4" /> Add Medicine
        </button>
      </div>

      {loadError && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
          Could not load your medicines from the server. Showing locally saved reminders only — please refresh.
        </div>
      )}

      {/* Today's Schedule */}
      {todayDoses.length > 0 && (
        <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-2xl p-4 border border-teal-100">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-teal-800">Today's Doses</h2>
            <span className="text-xs text-teal-600">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
          </div>
          {/* Daily progress bar */}
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 h-2 bg-white/60 rounded-full overflow-hidden">
              <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${todayDoses.length ? Math.round((todayDoses.filter(d => d._status === 'TAKEN').length / todayDoses.length) * 100) : 0}%` }} />
            </div>
            <span className="text-[11px] font-medium text-teal-700">{todayDoses.filter(d => d._status === 'TAKEN').length}/{todayDoses.length}</span>
          </div>
          <div className="flex gap-3 overflow-x-auto pb-1">
            {todayDoses.map((d, i) => (
              <div key={`${d.id}-${d._time}-${i}`} className={`shrink-0 w-44 rounded-xl p-3 border ${d._status === 'TAKEN' ? 'bg-green-50 border-green-200' : d._status === 'SKIPPED' ? 'bg-gray-50 border-gray-200' : d._status === 'MISSED' ? 'bg-red-50 border-red-200' : 'bg-white border-gray-100'}`}>
                <div className="flex items-center gap-2 mb-1.5">
                  <Pill className={`w-4 h-4 ${d._status === 'TAKEN' ? 'text-green-600' : d._status === 'SKIPPED' ? 'text-gray-400' : d._status === 'MISSED' ? 'text-red-500' : 'text-teal-600'}`} />
                  <span className="text-xs font-semibold text-gray-900 truncate">{d.medicineName}</span>
                </div>
                <p className="text-[11px] text-gray-500 mb-0.5">{d.dosage} · {d._time}</p>
                {d.instructions && (
                  <span className={`inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-full mb-1.5 ${d.instructions.toLowerCase().includes('before') ? 'bg-orange-50 text-orange-600' : d.instructions.toLowerCase().includes('after') ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600'}`}>
                    {d.instructions}
                  </span>
                )}
                {d._status === 'PENDING' ? (
                  <div className="flex gap-1.5">
                    <button onClick={() => handleLog(d.id, true, d._time)} className="flex-1 flex items-center justify-center gap-1 py-1 rounded-lg bg-green-100 text-green-700 text-[11px] font-medium hover:bg-green-200"><Check className="w-3 h-3" />Taken</button>
                    <button onClick={() => setSkipModal({ id: d.id, time: d._time })} className="flex-1 flex items-center justify-center gap-1 py-1 rounded-lg bg-gray-100 text-gray-500 text-[11px] font-medium hover:bg-gray-200"><X className="w-3 h-3" />Skip</button>
                  </div>
                ) : d._status === 'TAKEN' ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-[11px] text-green-600 font-medium"><Check className="w-3.5 h-3.5" />Taken</div>
                    <button onClick={() => handleLog(d.id, false, d._time)} className="p-1 text-gray-300 hover:text-amber-500 transition-colors" title="Undo"><Undo2 className="w-3 h-3" /></button>
                  </div>
                ) : d._status === 'SKIPPED' ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-[11px] text-gray-400 font-medium"><X className="w-3.5 h-3.5" />Skipped</div>
                    <button onClick={() => handleLog(d.id, true, d._time)} className="p-1 text-gray-300 hover:text-green-500 transition-colors" title="Mark as taken"><Undo2 className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-[11px] text-red-500 font-medium"><AlertTriangle className="w-3.5 h-3.5" />Missed</div>
                    <button onClick={() => handleLog(d.id, true, d._time)} className="px-2 py-0.5 text-[10px] font-medium text-green-700 bg-green-100 rounded-full hover:bg-green-200">Take now</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* A prescribed course that never became a reminder would otherwise be
          invisible on a page that looks like the full list. */}
      <PrescribedNotTracked
        trackedNames={new Set(meds.map((m) => (m.medicineName || '').trim().toLowerCase()))}
        onTracked={() => { void load(); }}
      />

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Active Medicines', value: activeMeds.length, color: 'text-teal-700 bg-teal-50' },
          { label: "Today's Doses", value: todayDoses.length, color: 'text-blue-700 bg-blue-50' },
          { label: 'Adherence (7d)', value: totalAdherence === null ? 'No doses logged' : `${totalAdherence}%`, color: 'text-emerald-700 bg-emerald-50' },
          { label: 'Prescribed', value: prescribedCount, color: 'text-purple-700 bg-purple-50' },
        ].map(s => (
          <div key={s.label} className="rounded-xl border border-gray-100 p-3 bg-white">
            <p className="text-[11px] text-gray-500 mb-1">{s.label}</p>
            <p className={`text-lg font-bold ${s.color.split(' ')[0]}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
        {tabs.map(t => (
          <button key={t} onClick={() => setTab(t)} className={`flex-1 py-2 text-xs font-medium rounded-lg transition-colors whitespace-nowrap ${tab === t ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t === 'ALL' ? 'All' : t === 'PRESCRIBED' ? 'Prescribed' : KIND_LABEL[t] ?? t.charAt(0) + t.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {/* Medicine List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <Pill className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No medicines found</p>
          <p className="text-sm text-gray-400 mt-1">Add your daily medications to track adherence</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filtered.map(med => {
            const times = parseTimes(med.times);
            const rate = adherenceRate(med.adherenceLogs);
            const isPrescribed = med.source === 'PRESCRIPTION';
            return (
              <div key={med.id} className="bg-white rounded-2xl border border-gray-100 p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-bold text-gray-900">{med.medicineName}</p>
                      {/* A diet goal is not a dose. Saying so is the whole
                          point of `kind`, which this page used to drop. */}
                      {med.kind && med.kind !== 'MEDICINE' && (
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${med.kind === 'DIET' ? 'bg-lime-50 text-lime-700' : 'bg-indigo-50 text-indigo-700'}`}>
                          {KIND_LABEL[med.kind] ?? med.kind}
                        </span>
                      )}
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${med.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{freqLabel(med.frequency)}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{med.dosage}</p>
                  </div>
                  <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full shrink-0 ${isPrescribed ? 'bg-teal-50 text-teal-700' : 'bg-gray-100 text-gray-500'}`}>
                    {isPrescribed ? 'Prescribed' : 'Manual'}
                  </span>
                </div>

                {med.instructions && (
                  <span className="inline-block mt-2 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{med.instructions}</span>
                )}

                {/* Only a real pharmacy order says a medicine was dispensed —
                    never inferred from the name or the course length. */}
                {med.fulfilment && (
                  <div className="mt-2 text-[11px] text-emerald-700 bg-emerald-50 rounded-lg px-2.5 py-1.5 leading-relaxed">
                    Dispensed by {med.fulfilment.pharmacyName ?? 'a pharmacy'} · {med.fulfilment.orderNumber}
                    {med.fulfilment.unitsDispensed != null && ` · ${med.fulfilment.unitsDispensed} units`}
                    {med.fulfilment.refillDueOn && ` · runs out ${med.fulfilment.refillDueOn}`}
                  </div>
                )}

                {isPrescribed && med.doctorName && (
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-teal-700">
                    <Stethoscope className="w-3.5 h-3.5" />
                    <span>Prescribed by {med.doctorName}</span>
                    {med.prescriptionId && <ChevronRight className="w-3 h-3 text-teal-400" />}
                  </div>
                )}

                {(med.startDate || med.endDate) && (
                  <div className="flex items-center gap-1.5 mt-2 text-[11px] text-gray-400">
                    <Calendar className="w-3 h-3" />
                    <span>{med.startDate?.split('T')[0] || '—'} → {med.endDate?.split('T')[0] || 'Ongoing'}</span>
                  </div>
                )}

                {isRefillSoon(med) && (
                  <div className="flex items-center gap-1.5 mt-2 text-[11px] font-medium text-amber-600 bg-amber-50 px-2 py-1 rounded-lg">
                    <AlertTriangle className="w-3.5 h-3.5" /> Refill needed soon
                  </div>
                )}

                {times.length > 0 && (
                  <div className="flex items-center gap-1 mt-2">
                    <Clock className="w-3 h-3 text-gray-400" />
                    <span className="text-[11px] text-gray-400">{times.join(', ')}</span>
                  </div>
                )}

                {/* 7-day adherence */}
                <div className="flex items-center gap-1 mt-3">
                  <span className="text-[10px] text-gray-400 mr-1">7d:</span>
                  {/* One square per CALENDAR DAY, not per log entry. Indexing
                      the log array painted the seven most recent logs — from
                      any dates at all — as though they were this week, so a
                      course logged months ago showed a perfect row of green. */}
                  {last7Days().map(day => {
                    const dayLogs = (med.adherenceLogs || []).filter(l => (l.logDate || '').slice(0, 10) === day);
                    const label = new Date(day).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
                    if (!dayLogs.length) {
                      return <div key={day} className="w-4 h-4 rounded-sm bg-gray-200" title={`${label} — nothing logged`} />;
                    }
                    const allTaken = dayLogs.every(l => l.taken);
                    const noneTaken = dayLogs.every(l => !l.taken);
                    return (
                      <div
                        key={day}
                        className={`w-4 h-4 rounded-sm ${allTaken ? 'bg-green-400' : noneTaken ? 'bg-red-400' : 'bg-amber-400'}`}
                        title={`${label} — ${dayLogs.filter(l => l.taken).length}/${dayLogs.length} taken`}
                      />
                    );
                  })}
                  {rate !== null && <span className="text-[10px] text-gray-500 ml-1">{rate}%</span>}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
                  {med.status === 'ACTIVE' && (
                    <div className="flex gap-2">
                      <button onClick={() => handleLog(med.id, true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-50 text-green-700 text-xs font-medium hover:bg-green-100 transition-colors">
                        <Check className="w-3.5 h-3.5" /> Taken today
                      </button>
                      <button onClick={() => handleLog(med.id, false)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-50 text-gray-500 text-xs font-medium hover:bg-gray-100 transition-colors">
                        <X className="w-3.5 h-3.5" /> Skipped
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-1 ml-auto">
                    <button onClick={() => openEdit(med)} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"><Edit3 className="w-4 h-4" /></button>
                    {isPrescribed ? (
                      <span className="text-[10px] text-teal-500 px-2">From prescription</span>
                    ) : (
                      <button onClick={() => handleDelete(med.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Skip Reason Modal */}
      {skipModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end justify-center" onClick={() => setSkipModal(null)}>
          <div className="bg-white rounded-t-2xl w-full max-w-md p-5 pb-8" onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-4" />
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Why are you skipping?</h3>
            <p className="text-xs text-gray-400 mb-4">This helps track patterns for your doctor</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {SKIP_REASONS.map(reason => (
                <button key={reason} onClick={() => { handleLog(skipModal.id, false, skipModal.time, reason); setSkipModal(null); }} className="px-3 py-2 rounded-xl bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200 transition-colors">
                  {reason}
                </button>
              ))}
            </div>
            <button onClick={() => setSkipModal(null)} className="w-full py-2.5 text-sm text-gray-500 font-medium">Cancel</button>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">{editId ? 'Edit' : 'Add'} Medicine</h2>
              <button onClick={() => setShowForm(false)} className="p-1 text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="label">Medicine Name *</label>
                <input className="input" value={form.medicineName} onChange={e => setForm(f => ({ ...f, medicineName: e.target.value }))} placeholder="e.g. Metformin 500mg" />
              </div>
              <div>
                <label className="label">Dosage *</label>
                <input className="input" value={form.dosage} onChange={e => setForm(f => ({ ...f, dosage: e.target.value }))} placeholder="e.g. 1 tablet" />
              </div>
              <div>
                <label className="label">Frequency</label>
                <select className="input" value={form.frequency} onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}>
                  {FREQUENCIES.map(fr => <option key={fr} value={fr}>{fr.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Times</label>
                <div className="space-y-2">
                  {form.times.map((t, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input type="time" className="input flex-1" value={t} onChange={e => updateTime(i, e.target.value)} />
                      {form.times.length > 1 && <button onClick={() => removeTimeSlot(i)} className="p-1 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>}
                    </div>
                  ))}
                  <button onClick={addTimeSlot} className="text-xs text-teal-600 hover:text-teal-700 font-medium">+ Add time</button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Start Date</label>
                  <input type="date" className="input" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
                </div>
                <div>
                  <label className="label">End Date</label>
                  <input type="date" className="input" value={form.endDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="label">Instructions</label>
                <select className="input" value={form.instructions} onChange={e => setForm(f => ({ ...f, instructions: e.target.value }))}>
                  <option value="">Select instruction...</option>
                  {INSTRUCTIONS.map(inst => <option key={inst} value={inst}>{inst}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea className="input" rows={2} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes..." />
              </div>
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-3">
              <button onClick={() => setShowForm(false)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleSave} disabled={saving || !form.medicineName || !form.dosage} className="btn-primary flex-1 flex items-center justify-center gap-2">
                {saving ? <Spinner size="sm" /> : editId ? 'Update' : 'Add Medicine'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
